import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import "../../asset/css/styles.css";
import BackdateConfigSetting from "../BackdateConfigSetting/page";

function NavbarVertical({ isOpen, onToggle }) {
  const navigate = useNavigate();
  const location = useLocation();
  const pathname = location.pathname;
  const roleId = parseInt(localStorage.getItem("roleId")); // 1=SUPERADMIN,2=ADMIN,3=EMPLOYEE,4=USER,5=HR,6=TEAM LEAD

  const getTabFromUrl = () =>
    new URLSearchParams(location.search).get("tab") || "activeSprint";
  const [activeSprintTab, setActiveSprintTab] = useState(getTabFromUrl);

  useEffect(() => { setActiveSprintTab(getTabFromUrl()); }, [location.search]);

  useEffect(() => {
    const handler = (e) => setActiveSprintTab(e.detail);
    window.addEventListener("changeSprintTab", handler);
    return () => window.removeEventListener("changeSprintTab", handler);
  }, []);

  useEffect(() => {
    const handler = () => onToggle();
    window.addEventListener("toggleSidebar", handler);
    return () => window.removeEventListener("toggleSidebar", handler);
  }, [onToggle]);

  const [expanded, setExpanded] = useState(() => {
    if (pathname.startsWith("/onboarddeboard/employees")) return "employees";
    if (pathname.startsWith("/onboarddeboard/master")) return "master";
    if (pathname.startsWith("/onboarddeboard/leave")) return "leave";
    if (pathname.startsWith("/onboarddeboard/ticketing")) return "ticketing";
    if (pathname.startsWith("/onboarddeboard/system")) return "system";
    if (pathname.startsWith("/reporting") && pathname !== "/reporting/Dashboard") return "reports";
    return "sprints";
  });
  const [hovered, setHovered] = useState(null);
  const [showHelp, setShowHelp] = useState(false);
  const [showBackdate, setShowBackdate] = useState(false);
  const [leaveMasterOpen, setLeaveMasterOpen] = useState(
    pathname.startsWith("/onboarddeboard/leave/master")
  );
  const [shiftAuthOpen, setShiftAuthOpen] = useState(
    pathname.startsWith("/onboarddeboard/leave/shift-authorities")
  );
  const [shiftMgmtOpen, setShiftMgmtOpen] = useState(
    pathname.startsWith("/onboarddeboard/leave/shift-management")
  );
  const [leaveDetailsOpen, setLeaveDetailsOpen] = useState(
    pathname.startsWith("/onboarddeboard/leave/leave-details")
  );
  const [leaveApplicationOpen, setLeaveApplicationOpen] = useState(
    pathname.startsWith("/onboarddeboard/leave/leave-application")
  );

  const toggleMenu = (key) => setExpanded((prev) => (prev === key ? null : key));

  const goSprintTab = (tab) => {
    setActiveSprintTab(tab);
    if (pathname === "/admin-dashboard") {
      window.dispatchEvent(new CustomEvent("changeSprintTab", { detail: tab }));
    } else {
      navigate("/admin-dashboard?tab=" + tab);
    }
  };

  const openSettings = () => window.dispatchEvent(new CustomEvent("openSettingsPanel"));


  const BLUE = "#2563eb";
  const ACTIVE_BG = "#2E5CB8";
  const SUB_ACTIVE = "#eff6ff";
  const HOVER = "#2E5CB8";
  const SUB_HOVER = "#eff6ff";
  const TEXT = "#374151";
  const MUTED = "#6b7280";
  const DIVIDER = "#f1f5f9";
  const sideW = isOpen ? 240 : 60;

  const h = (id) => ({
    onMouseEnter: () => setHovered(id),
    onMouseLeave: () => setHovered(null),
  });

  const isPath = (p) => pathname === p;
  const startsWith = (p) => pathname.startsWith(p);
  const isSprintTab = (tab) => pathname === "/admin-dashboard" && activeSprintTab === tab;
  const isHL = (id, activeCond) => hovered === id || activeCond;


  const itemStyle = (id, activeCond) => ({
    background: activeCond ? ACTIVE_BG : hovered === id ? HOVER : "transparent",
    color: isHL(id, activeCond) ? "#fff" : TEXT,
    fontWeight: activeCond ? 600 : 500,
  });
  const iconColor = (id, activeCond) => isHL(id, activeCond) ? "#fff" : MUTED;
  const subStyle = (id, activeCond) => ({
    background: activeCond ? SUB_ACTIVE : hovered === id ? SUB_HOVER : "transparent",
    color: activeCond ? "#2E5CB8" : hovered === id ? "#2E5CB8" : MUTED,
    fontWeight: activeCond ? 600 : 400,
  });
  const dotColor = (id, activeCond) => activeCond ? "#2E5CB8" : hovered === id ? "#2E5CB8" : "#d1d5db";
  const chevronColor = (id, activeCond) => isHL(id, activeCond) ? "#fff" : undefined;

  const employeeLinks = [
    { id: "emp-list", label: "Employees List", path: "/onboarddeboard/employees/list" },
    { id: "emp-att", label: "Attendance", path: "/onboarddeboard/employees/attendance" },
    { id: "emp-leave", label: "Leave", path: "/onboarddeboard/employees/leave" },
    { id: "emp-pay", label: "Payroll", path: "/onboarddeboard/employees/payroll" },
    { id: "emp-perf", label: "Performance", path: "/onboarddeboard/employees/performance" },
  ];

  return (
    <>
      {/* Help modal */}
      {showHelp && (
        <div className="hrnav-overlay" onClick={() => setShowHelp(false)}>
          <div className="hrnav-modal" onClick={(e) => e.stopPropagation()}>
            <div className="hrnav-modal-head">
              <span className="hrnav-modal-title">
                <i className="bi bi-question-circle-fill" style={{ marginRight: 8 }}></i>
                Help Center
              </span>
              <button className="hrnav-modal-close" onClick={() => setShowHelp(false)}>
                <i className="bi bi-x-lg"></i>
              </button>
            </div>
            <div className="hrnav-modal-body">
              {[
                { icon: "bi-lightning-charge-fill", bg: "#eff6ff", color: "#2563eb", title: "Sprint Management", desc: "Create sprints, assign tasks to Planning/Backlog, track Active and Completed sprints." },
                { icon: "bi-people-fill", bg: "#fce7f3", color: "#db2777", title: "Employee Information", desc: "View and manage all employee records under Employees → Employees List." },
                { icon: "bi-bar-chart-fill", bg: "#d1fae5", color: "#059669", title: "Reports", desc: "Access Daily and Work Nature reports from the Reports menu." },
                { icon: "bi-briefcase-fill", bg: "#fef3c7", color: "#d97706", title: "Master Data", desc: "Configure Departments, Designations, Banks, Documents and Appointments under Master." },
                { icon: "bi-shield-lock-fill", bg: "#f5f3ff", color: "#7c3aed", title: "Change Password", desc: "Update your password anytime via the user menu (top-right) → Change Password." },
              ].map((item, i) => (
                <div key={i} className="hrnav-help-row">
                  <div className="hrnav-help-icon" style={{ background: item.bg, color: item.color }}>
                    <i className={`bi ${item.icon}`}></i>
                  </div>
                  <div>
                    <div className="hrnav-help-title">{item.title}</div>
                    <div className="hrnav-help-desc">{item.desc}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      <nav
        className="hrnav"
        style={{
          position: "fixed", top: 0, left: 0,
          width: sideW, height: "100vh",
          background: "#fff", borderRight: "1px solid #e5e7eb",
          display: "flex", flexDirection: "column",
          transition: "width 0.25s ease",
          zIndex: 1050, overflowX: "hidden", overflowY: "auto",
          boxShadow: "2px 0 8px rgba(0,0,0,0.04)",
        }}
      >
        {/* Header */}
        <div style={{
          display: "flex", alignItems: "center",
          justifyContent: isOpen ? "space-between" : "center",
          padding: isOpen ? "16px 16px 12px" : "16px 0 12px",
          borderBottom: `1px solid ${DIVIDER}`, minHeight: 56, flexShrink: 0,
        }}>
          {isOpen && (
            <span style={{ fontSize: 13, fontWeight: 700, color: "#1e293b", letterSpacing: "-0.2px", whiteSpace: "nowrap" }}>
              Global Solution's <span style={{ color: BLUE }}>Orbit</span>
            </span>
          )}
          <button
            onClick={onToggle}
            title={isOpen ? "Collapse" : "Expand"}
            style={{ background: "none", border: "none", cursor: "pointer", color: MUTED, fontSize: 18, display: "flex", alignItems: "center", justifyContent: "center", borderRadius: 6, width: 28, height: 28, flexShrink: 0 }}
          >
            <i className="bi bi-list"></i>
          </button>
        </div>

        <div style={{ flex: 1, padding: "8px 0" }}>

          {/* Dashboard — Admin(1,2) + HR(5) + TeamLead(6) */}
          {[1, 2, 5, 6].includes(roleId) && (
            <div className={`hrnav-item${!isOpen ? " ico" : ""}`}
              style={itemStyle("dash", isPath("/reporting/Dashboard"))}
              onClick={() => navigate("/reporting/Dashboard")}
              title={!isOpen ? "Dashboard" : undefined} {...h("dash")}>
              <i className="bi bi-grid-1x2-fill hrnav-icon" style={{ color: iconColor("dash", isPath("/reporting/Dashboard")) }}></i>
              {isOpen && <span style={{ flex: 1 }}>Dashboard</span>}
            </div>
          )}

          {/* Master — Admin(1,2) + HR(5) only */}
          {[1, 2, 5].includes(roleId) && (
            <>
              <div className={`hrnav-item${!isOpen ? " ico" : ""}`}
                style={itemStyle("master", startsWith("/onboarddeboard/master"))}
                onClick={() => { toggleMenu("master"); if (!isOpen) navigate("/onboarddeboard/master/departments"); }}
                title={!isOpen ? "Master" : undefined} {...h("master")}>
                <i className="bi bi-briefcase-fill hrnav-icon" style={{ color: iconColor("master", startsWith("/onboarddeboard/master")) }}></i>
                {isOpen && (
                  <>
                    <span style={{ flex: 1 }}>Master</span>
                    <i className={`bi bi-chevron-right hrnav-chevron${expanded === "master" ? " open" : ""}`}
                      style={{ color: chevronColor("master", startsWith("/onboarddeboard/master")) }}></i>
                  </>
                )}
              </div>
              {isOpen && expanded === "master" && [
                { id: "m-dept", label: "Department", path: "/onboarddeboard/master/departments" },
                { id: "m-desig", label: "Designation", path: "/onboarddeboard/master/designation" },
                { id: "m-bank", label: "Bank", path: "/onboarddeboard/master/bank" },
                { id: "m-doc", label: "Document", path: "/onboarddeboard/master/document" },
                { id: "m-appt", label: "Appointment", path: "/onboarddeboard/master/appoint" },
                { id: "m-module", label: "Module Master", path: "/onboarddeboard/master/module" },
              ].map(({ id, label, path }) => (
                <div key={id} className="hrnav-sub" style={subStyle(id, isPath(path))} onClick={() => navigate(path)} {...h(id)}>
                  <span className="hrnav-dot" style={{ background: dotColor(id, isPath(path)) }}></span>
                  {label}
                </div>
              ))}
            </>
          )}

          {/* Employees — Admin(1,2) + HR(5) only */}
          {[1, 2, 5].includes(roleId) && (
            <>
              <div className={`hrnav-item${!isOpen ? " ico" : ""}`}
                style={itemStyle("emp", startsWith("/onboarddeboard/employees"))}
                onClick={() => { toggleMenu("employees"); if (!isOpen) navigate("/onboarddeboard/employees/list"); }}
                title={!isOpen ? "Employees" : undefined} {...h("emp")}>
                <i className="bi bi-people-fill hrnav-icon" style={{ color: iconColor("emp", startsWith("/onboarddeboard/employees")) }}></i>
                {isOpen && (
                  <>
                    <span style={{ flex: 1 }}>Employees</span>
                    <i className={`bi bi-chevron-right hrnav-chevron${expanded === "employees" ? " open" : ""}`}
                      style={{ color: chevronColor("emp", startsWith("/onboarddeboard/employees")) }}></i>
                  </>
                )}
              </div>
              {isOpen && expanded === "employees" && employeeLinks.map(({ id, label, path }) => (
                <div key={id} className="hrnav-sub" style={subStyle(id, isPath(path))} onClick={() => navigate(path)} {...h(id)}>
                  <span className="hrnav-dot" style={{ background: dotColor(id, isPath(path)) }}></span>
                  {label}
                </div>
              ))}
            </>
          )}

          {/* Sprints — Admin(1,2) + TeamLead(6) only */}
          {[1, 2, 6].includes(roleId) && (
            <>
              <div className={`hrnav-item${!isOpen ? " ico" : ""}`}
                style={itemStyle("sprints", pathname === "/admin-dashboard")}
                onClick={() => { toggleMenu("sprints"); if (!isOpen) navigate("/admin-dashboard"); }}
                title={!isOpen ? "Sprints" : undefined} {...h("sprints")}>
                <i className="bi bi-lightning-charge-fill hrnav-icon" style={{ color: iconColor("sprints", pathname === "/admin-dashboard") }}></i>
                {isOpen && (
                  <>
                    <span style={{ flex: 1 }}>Sprints</span>
                    <i className={`bi bi-chevron-right hrnav-chevron${expanded === "sprints" ? " open" : ""}`}
                      style={{ color: chevronColor("sprints", pathname === "/admin-dashboard") }}></i>
                  </>
                )}
              </div>
              {isOpen && expanded === "sprints" && [
                { id: "sp-plan", label: "Planning / Backlog", tab: "planning" },
                { id: "sp-active", label: "Active Sprint", tab: "activeSprint" },
                { id: "sp-done", label: "Completed Sprint", tab: "completed" },
              ].map(({ id, label, tab }) => {
                const act = isSprintTab(tab);
                return (
                  <div key={id} className="hrnav-sub" style={subStyle(id, act)} onClick={() => goSprintTab(tab)} {...h(id)}>
                    <span className="hrnav-dot" style={{ background: dotColor(id, act) }}></span>
                    {label}
                  </div>
                );
              })}
            </>
          )}

          {/* Reports — Admin(1,2) + HR(5) see all 3; TeamLead(6) sees Daily Report + Employee Daily Report */}
          {[1, 2, 5, 6].includes(roleId) && (
            <>
              <div className={`hrnav-item${!isOpen ? " ico" : ""}`}
                style={itemStyle("reports", startsWith("/reporting") && !isPath("/reporting/Dashboard"))}
                onClick={() => { toggleMenu("reports"); if (!isOpen) navigate("/reporting/admindailyreport"); }}
                title={!isOpen ? "Reporting" : undefined} {...h("reports")}>
                <i className="bi bi-bar-chart-fill hrnav-icon" style={{ color: iconColor("reports", startsWith("/reporting") && !isPath("/reporting/Dashboard")) }}></i>
                {isOpen && (
                  <>
                    <span style={{ flex: 1 }}>Reporting</span>                    <i className={`bi bi-chevron-right hrnav-chevron${expanded === "reports" ? " open" : ""}`}
                      style={{ color: chevronColor("reports", startsWith("/reporting") && !isPath("/reporting/Dashboard")) }}></i>
                  </>
                )}
              </div>
              {isOpen && expanded === "reports" && [

                { id: "rep-emp-daily", label: "Daily Report", path: "/reporting/admindailyreport", showFor: [1, 2, 5, 6] },
                { id: "rep-my", label: "My Reports", path: "/reporting/myreports", showFor: [5, 6] },
                { id: "rep-bd-req", label: "Backdated Reporting Request", path: "/reporting/backdaterequest", showFor: [5, 6] },
                { id: "rep-bd-appr", label: "Backdated Reporting", path: "/reporting/backdateapproval", showFor: [1, 2] },
                { id: "rep-daily", label: "Admin Report", path: "/reporting/dailyreport", showFor: [1, 2, 5] }, { id: "rep-work", label: "Work Nature Master", path: "/reporting/worknature", showFor: [1, 2, 5] },


              ].filter(item => item.showFor.includes(roleId)).map(({ id, label, path }) => (
                <div key={id} className="hrnav-sub" style={subStyle(id, isPath(path))} onClick={() => navigate(path)} {...h(id)}>
                  <span className="hrnav-dot" style={{ background: dotColor(id, isPath(path)) }}></span>
                  {label}
                </div>
              ))}
            </>
          )}

          {/* Leave — Master/Shift&Auth/Shift Mgmt/Leave Details sub-groups stay Admin(1,2)+HR(5) only per note.
              Leave Application sub-group (added below) additionally opens this whole menu to Team Lead (6),
              since Team Lead is an approval authority for Leave/Compensatory approvals.
              Note: roleId 3 (EMPLOYEE) never renders this sidebar — see employeeNavbarVertical instead. */}
          {[1, 2, 5, 6].includes(roleId) && (
            <>
              <div className={`hrnav-item${!isOpen ? " ico" : ""}`}
                style={itemStyle("leave", startsWith("/onboarddeboard/leave"))}
                onClick={() => { toggleMenu("leave"); if (!isOpen) navigate("/onboarddeboard/leave/master/leave-cycle"); }}
                title={!isOpen ? "Leave" : undefined} {...h("leave")}>
                <i className="bi bi-calendar2-week-fill hrnav-icon" style={{ color: iconColor("leave", startsWith("/onboarddeboard/leave")) }}></i>
                {isOpen && (
                  <>
                    <span style={{ flex: 1 }}>Leave</span>
                    <i className={`bi bi-chevron-right hrnav-chevron${expanded === "leave" ? " open" : ""}`}
                      style={{ color: chevronColor("leave", startsWith("/onboarddeboard/leave")) }}></i>
                  </>
                )}
              </div>

              {isOpen && expanded === "leave" && (
                <div style={{ paddingLeft: 8 }}>
                  {/* Master — nested, expands further */}
                  {[1, 2, 5].includes(roleId) && (
                    <>
                      <div className="hrnav-sub" style={subStyle("leave-master", startsWith("/onboarddeboard/leave/master"))}
                        onClick={() => setLeaveMasterOpen((v) => !v)} {...h("leave-master")}>
                        <i className="bi bi-calendar2-week-fill" style={{ fontSize: 11, color: dotColor("leave-master", startsWith("/onboarddeboard/leave/master")), width: 14, textAlign: "center" }}></i>
                        <span style={{ flex: 1 }}>Master</span>
                        <i className={`bi bi-chevron-right hrnav-chevron${leaveMasterOpen ? " open" : ""}`}></i>
                      </div>
                      {leaveMasterOpen && (
                        <div style={{ paddingLeft: 16 }}>
                          {[
                            { id: "lm-cycle", label: "Leave Cycle", path: "/onboarddeboard/leave/master/leave-cycle" },
                            { id: "lm-htype", label: "Holiday Types", path: "/onboarddeboard/leave/master/holiday-types" },
                            { id: "lm-hentry", label: "Holiday Entry", path: "/onboarddeboard/leave/master/holiday-entry" },
                            { id: "lm-ltype", label: "Leave Type", path: "/onboarddeboard/leave/master/leave-type" },
                            { id: "lm-alloc", label: "Leave Type Allotment", path: "/onboarddeboard/leave/master/leave-type-allotment" },
                          ].map(({ id, label, path }) => (
                            <div key={id} className="hrnav-sub" style={subStyle(id, isPath(path))}
                              onClick={() => navigate(path)} {...h(id)}>
                              <span className="hrnav-dot" style={{ background: dotColor(id, isPath(path)) }}></span>
                              {label}
                            </div>
                          ))}
                        </div>
                      )}
                    </>
                  )}

                  {/* Shift & Authorities — Admin/HR */}
                  {[1, 2, 5].includes(roleId) && (
                    <>
                      <div className="hrnav-sub" style={subStyle("leave-shiftauth", startsWith("/onboarddeboard/leave/shift-authorities"))}
                        onClick={() => setShiftAuthOpen((v) => !v)} {...h("leave-shiftauth")}>
                        <i className="bi bi-calendar2-week-fill" style={{ fontSize: 11, color: dotColor("leave-shiftauth", startsWith("/onboarddeboard/leave/shift-authorities")), width: 14, textAlign: "center" }}></i>
                        <span style={{ flex: 1 }}>Shift & Authorities</span>
                        <i className={`bi bi-chevron-right hrnav-chevron${shiftAuthOpen ? " open" : ""}`}></i>
                      </div>
                      {shiftAuthOpen && (
                        <div style={{ paddingLeft: 16 }}>
                          {[
                            { id: "sa-shift", label: "Shift", path: "/onboarddeboard/leave/shift-authorities/shift" },
                            { id: "sa-auth", label: "Authorities", path: "/onboarddeboard/leave/shift-authorities/authorities" },
                            { id: "sa-path", label: "Authority Path", path: "/onboarddeboard/leave/shift-authorities/authority-path" },
                          ].map(({ id, label, path }) => (
                            <div key={id} className="hrnav-sub" style={subStyle(id, isPath(path))}
                              onClick={() => navigate(path)} {...h(id)}>
                              <span className="hrnav-dot" style={{ background: dotColor(id, isPath(path)) }}></span>
                              {label}
                            </div>
                          ))}
                        </div>
                      )}
                    </>
                  )}

                  {/* Shift Management — Admin/HR */}
                  {[1, 2, 5].includes(roleId) && (
                    <>
                      <div className="hrnav-sub" style={subStyle("leave-shiftmgmt", startsWith("/onboarddeboard/leave/shift-management"))}
                        onClick={() => setShiftMgmtOpen((v) => !v)} {...h("leave-shiftmgmt")}>
                        <i className="bi bi-calendar2-week-fill" style={{ fontSize: 11, color: dotColor("leave-shiftmgmt", startsWith("/onboarddeboard/leave/shift-management")), width: 14, textAlign: "center" }}></i>
                        <span style={{ flex: 1 }}>Shift Management</span>
                        <i className={`bi bi-chevron-right hrnav-chevron${shiftMgmtOpen ? " open" : ""}`}></i>
                      </div>
                      {shiftMgmtOpen && (
                        <div style={{ paddingLeft: 16 }}>
                          {[
                            { id: "sm-alloc", label: "Shift Allotment", path: "/onboarddeboard/leave/shift-management/shift-allotment" },
                            { id: "sm-changes", label: "Shift Changes", path: "/onboarddeboard/leave/shift-management/shift-changes" },
                          ].map(({ id, label, path }) => (
                            <div key={id} className="hrnav-sub" style={subStyle(id, isPath(path))}
                              onClick={() => navigate(path)} {...h(id)}>
                              <span className="hrnav-dot" style={{ background: dotColor(id, isPath(path)) }}></span>
                              {label}
                            </div>
                          ))}
                        </div>
                      )}
                    </>
                  )}

                  {/* Leave Details — Admin/HR */}
                  {[1, 2, 5].includes(roleId) && (
                    <>
                      <div className="hrnav-sub" style={subStyle("leave-details", startsWith("/onboarddeboard/leave/leave-details"))}
                        onClick={() => setLeaveDetailsOpen((v) => !v)} {...h("leave-details")}>
                        <i className="bi bi-calendar2-week-fill" style={{ fontSize: 11, color: dotColor("leave-details", startsWith("/onboarddeboard/leave/leave-details")), width: 14, textAlign: "center" }}></i>
                        <span style={{ flex: 1 }}>Leave Details</span>
                        <i className={`bi bi-chevron-right hrnav-chevron${leaveDetailsOpen ? " open" : ""}`}></i>
                      </div>
                      {leaveDetailsOpen && (
                        <div style={{ paddingLeft: 16 }}>
                          {[
                            { id: "ld-setup", label: "Leave Setup", path: "/onboarddeboard/leave/leave-details/leave-setup" },
                            { id: "ld-alloc", label: "Leave Allotment", path: "/onboarddeboard/leave/leave-details/leave-allotment" },
                          ].map(({ id, label, path }) => (
                            <div key={id} className="hrnav-sub" style={subStyle(id, isPath(path))}
                              onClick={() => navigate(path)} {...h(id)}>
                              <span className="hrnav-dot" style={{ background: dotColor(id, isPath(path)) }}></span>
                              {label}
                            </div>
                          ))}
                        </div>
                      )}
                    </>
                  )}

                  {/* Leave Application — NEW.
                      Employee Leave Application + Compensatory Leave Request: all roles (self-service).
                      Leave Approval + Compensatory Request Approval: Admin/SuperAdmin/HR/Team Lead (authorities).
                      Leave Cancellation: Admin/SuperAdmin only. */}
                  <div className="hrnav-sub" style={subStyle("leave-application", startsWith("/onboarddeboard/leave/leave-application"))}
                    onClick={() => setLeaveApplicationOpen((v) => !v)} {...h("leave-application")}>
                    <i className="bi bi-calendar2-week-fill" style={{ fontSize: 11, color: dotColor("leave-application", startsWith("/onboarddeboard/leave/leave-application")), width: 14, textAlign: "center" }}></i>
                    <span style={{ flex: 1 }}>Leave Application</span>
                    <i className={`bi bi-chevron-right hrnav-chevron${leaveApplicationOpen ? " open" : ""}`}></i>
                  </div>
                  {leaveApplicationOpen && (
                    <div style={{ paddingLeft: 8 }}>
                      {[
                        { id: "la-apply", label: "Employee Leave Application", path: "/onboarddeboard/leave/leave-application/apply", roles: [1, 2, 3, 5, 6] },
                        { id: "la-approval", label: "Leave Approval", path: "/onboarddeboard/leave/leave-application/approval", roles: [1, 2, 5, 6] },
                        { id: "la-cancel", label: "Leave Cancellation", path: "/onboarddeboard/leave/leave-application/cancel", roles: [1, 2] },
                        { id: "la-comp", label: "Compensatory Leave Request", path: "/onboarddeboard/leave/leave-application/compensatory-request", roles: [1, 2, 3, 5, 6] },
                        { id: "la-compapproval", label: "Compensatory Request Approval", path: "/onboarddeboard/leave/leave-application/compensatory-approval", roles: [1, 2, 5, 6] },
                      ].filter((item) => item.roles.includes(roleId)).map(({ id, label, path }) => (
                        <div key={id} className="hrnav-sub"
                          style={{
                            ...subStyle(id, isPath(path)),
                            whiteSpace: "normal",
                            overflow: "visible",
                            textOverflow: "unset",
                            wordBreak: "break-word",
                            lineHeight: 1.3,
                            alignItems: "flex-start",
                          }}
                          onClick={() => navigate(path)} {...h(id)}>
                          <span className="hrnav-dot" style={{ background: dotColor(id, isPath(path)), marginTop: 5, flexShrink: 0 }}></span>
                          {label}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </>
          )}


          {/* Ticketing — Admin(1,2) + TeamLead(6) only */}
          {[1, 2, 6].includes(roleId) && (
            <div className={`hrnav-item${!isOpen ? " ico" : ""}`}
              style={itemStyle("ticketing", startsWith("/onboarddeboard/ticketing"))}
              onClick={() => navigate("/onboarddeboard/ticketing")}
              title={!isOpen ? "Ticketing" : undefined} {...h("ticketing")}>
              <i className="bi bi-ticket-perforated-fill hrnav-icon"
                style={{ color: iconColor("ticketing", startsWith("/onboarddeboard/ticketing")) }}></i>
              {isOpen && <span style={{ flex: 1 }}>Ticketing</span>}
            </div>
          )}

          {/* System — Admin(1,2) only, now expandable with Role Allocation */}
          {[1, 2].includes(roleId) && (
            <>
              <div className={`hrnav-item${!isOpen ? " ico" : ""}`}
                style={itemStyle("system", startsWith("/onboarddeboard/system"))}
                onClick={() => { toggleMenu("system"); if (!isOpen) navigate("/onboarddeboard/system/role-allocation"); }}
                title={!isOpen ? "System" : undefined} {...h("system")}>
                <i className="bi bi-cpu hrnav-icon" style={{ color: iconColor("system", startsWith("/onboarddeboard/system")) }}></i>
                {isOpen && (
                  <>
                    <span style={{ flex: 1 }}>System</span>
                    <i className={`bi bi-chevron-right hrnav-chevron${expanded === "system" ? " open" : ""}`}
                      style={{ color: chevronColor("system", startsWith("/onboarddeboard/system")) }}></i>
                  </>
                )}
              </div>
              {isOpen && expanded === "system" && [
                { id: "sys-role", label: "Role Allocation", path: "/onboarddeboard/system/role-allocation" },
              ].map(({ id, label, path }) => (
                <div key={id} className="hrnav-sub" style={subStyle(id, isPath(path))} onClick={() => navigate(path)} {...h(id)}>
                  <span className="hrnav-dot" style={{ background: dotColor(id, isPath(path)) }}></span>
                  {label}
                </div>
              ))}

              {isOpen && expanded === "system" && (
                <div className="hrnav-sub" style={subStyle("sys-backdate", showBackdate)}
                  onClick={() => setShowBackdate(true)} {...h("sys-backdate")}>
                  <span className="hrnav-dot" style={{ background: dotColor("sys-backdate", showBackdate) }}></span>
                  Backdate Configuration
                </div>
              )}
            </>
          )}
        </div>

        {/* Bottom — unchanged, all roles */}
        <div style={{ borderTop: `1px solid ${DIVIDER}`, padding: "8px 0", flexShrink: 0 }}>
          <div className={`hrnav-item${!isOpen ? " ico" : ""}`}
            style={{ color: hovered === "settings" ? "#fff" : TEXT, background: hovered === "settings" ? HOVER : "transparent" }}
            onClick={openSettings} title={!isOpen ? "Settings" : undefined} {...h("settings")}>
            <i className="bi bi-gear-fill hrnav-icon" style={{ color: hovered === "settings" ? "#fff" : MUTED }}></i>
            {isOpen && <span style={{ flex: 1 }}>Settings</span>}
          </div>

          <div className={`hrnav-item${!isOpen ? " ico" : ""}`}
            style={{ color: hovered === "help" ? "#fff" : TEXT, background: hovered === "help" ? HOVER : "transparent" }}
            onClick={() => setShowHelp(true)} title={!isOpen ? "Help Center" : undefined} {...h("help")}>
            <i className="bi bi-question-circle hrnav-icon" style={{ color: hovered === "help" ? "#fff" : MUTED }}></i>
            {isOpen && <span style={{ flex: 1 }}>Help Center</span>}
          </div>

          <div className={`hrnav-item${!isOpen ? " ico" : ""}`}
            style={{ color: hovered === "logout" ? "#fff" : "#ef4444", background: hovered === "logout" ? "#ef4444" : "transparent" }}
            onClick={() => { localStorage.clear(); window.location.href = "/"; }}
            title={!isOpen ? "Log Out" : undefined} {...h("logout")}>
            <i className="bi bi-box-arrow-right hrnav-icon" style={{ color: hovered === "logout" ? "#fff" : "#ef4444" }}></i>
            {isOpen && <span style={{ flex: 1 }}>Log Out</span>}
          </div>
        </div>
      </nav>

      <BackdateConfigSetting show={showBackdate} onHide={() => setShowBackdate(false)} />
    </>
  );
}

export default NavbarVertical;
